import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLock, FaShoppingCart, FaKey, FaPhoneAlt,FaCrown, FaArrowRight, FaShieldAlt, FaStar, FaGem, FaRocket, FaChartLine } from 'react-icons/fa';
import { motion, useInView } from 'framer-motion';
import icon1 from '../../assets/icons/295128.png';
import icon2 from '../../assets/icons/3135715.png';
import icon3 from '../../assets/icons/3135690.png';
import icon4 from '../../assets/icons/3135753.png';

const MemberLoginCTA = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true });

  return (
    <section ref={sectionRef} className="relative py-24 bg-gradient-to-b from-white to-blue-50 overflow-hidden">
      {/* Animated Background Assets */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Top Left Asset - Animated Lock */}
        <motion.div
          animate={{ 
            y: [0, -15, 0],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ 
            duration: 6, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
          className="absolute top-10 left-5 opacity-10"
        >
          <img 
            src={icon1} 
            alt="Security Lock" 
            className="w-20"
            height={20}
            width={20}
          />
        </motion.div>
        
        {/* Top Right Asset - Animated Chart */}
        <motion.div
          animate={{ 
            y: [0, 10, 0],
            x: [0, -5, 0],
            scale: [1, 1.05, 1]
          }}
          transition={{ 
            duration: 7, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
          className="absolute top-5 right-5 opacity-10"
        >
          <img 
            src={icon2}
            alt="Growth Chart" 
            className="w-24"
            height={24}
            width={24}
          />
        </motion.div>

        {/* Middle Left Asset - Portfolio */}
        <motion.div
          animate={{ 
            y: [0, -10, 0],
            rotate: [0, -3, 3, 0]
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute top-1/3 left-10 opacity-10"
        >
          <img 
            src={icon3}
            alt="Portfolio" 
            className="w-16"
            height={16}
            width={16}
          />
        </motion.div>

        {/* Bottom Right Asset - Analysis */}
        <motion.div
          animate={{ 
            y: [0, 8, 0],
            rotate: [0, 180, 360]
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity,
            ease: "linear",
            delay: 2
          }}
          className="absolute bottom-20 right-10 opacity-10"
        >
          <img 
            src={icon4}
            alt="Analysis" 
            className="w-20"
            height={20}
            width={20}
          />
        </motion.div>

        {/* Gradient Orbs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-100/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        

        {/* Animated Section Header */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="text-center mb-10"
                >
                  {/* Animated Premium Badge */}
                  <motion.div
                    viewport={{ once: true }}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-400/30 px-6 py-3 rounded-full mb-6 shadow-lg"
                  >
                    {/* <FaCrown className="text-yellow-400 animate-pulse" /> */}
                    <span className="font-bold">PREMIUM MEMBER ACCESS</span>
                    <FaGem className="text-purple-400 animate-pulse animation-delay-500" />
                  </motion.div>
                  
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    Access Your Premium Research Dashboard
                  </h2>
                  
                  {/* Animated Underline */}
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "100px" }}
                    transition={{ duration: 1, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mb-4 rounded-full"
                  />
                  
                  <p className=" text-lg mx-auto">
                    Ready to explore your exclusive member benefits? Login to access premium research reports, stock recommendations, and market insights.
                  </p>
                </motion.div>

        {/* Main Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-5 justify-center mb-16">
          {/* Login Button - Blue */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.3, delay: 0.2 }}
            whileHover={{ 
              scale: 1.03,
              y: -2,
              transition: { duration: 0.15 }
            }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/login')}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-150 flex items-center justify-center gap-3 group relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500 ease-out"></div>
            <FaLock className="text-lg relative z-10 group-hover:scale-110 transition-transform duration-150" />
            <span className="relative z-10">Member Login</span>
            <FaArrowRight className="text-sm relative z-10 group-hover:translate-x-1 transition-transform duration-150" />
          </motion.button>

          {/* Subscribe Button - Purple */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.3, delay: 0.3 }}
            whileHover={{ 
              scale: 1.03,
              y: -2,
              transition: { duration: 0.15 }
            }}
            whileTap={{ scale: 0.98 }}
            onClick={() => window.open('https://www.marketmaestroo.com', '_blank')}
            className="px-8 py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-150 flex items-center justify-center gap-3 group"
          >
            <FaShoppingCart className="group-hover:scale-110 transition-transform duration-150" />
            <span>Subscribe Now</span>
            <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform duration-150" />
          </motion.button>
        </div>

        {/* Enhanced Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mx-auto mb-10">
          {/* Card 1: Subscribe - Blue */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.3, delay: 0.4 }}
            whileHover={{ 
              y: -3,
              scale: 1.02,
              transition: { duration: 0.15 }
            }}
            className="relative group overflow-hidden rounded-2xl"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-blue-500 to-blue-600"></div>
            <div className="relative bg-gradient-to-br from-blue-500 to-blue-600 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-150">
              <div className="text-center">
                <motion.div
                  whileHover={{ 
                    scale: 1.05,
                    rotate: 5,
                    transition: { duration: 0.15 }
                  }}
                  className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6 backdrop-blur-sm"
                >
                  <FaShoppingCart className="text-white text-2xl" />
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-3">Not a Member Yet?</h3>
                <p className="text-blue-100 text-sm mb-6 leading-relaxed">
                  Subscribe to our SEBI RA Monthly Plans and get instant access to premium research.
                </p>
                <button
                  onClick={() => window.open('https://www.marketmaestroo.com', '_blank')}
                  className="text-white font-semibold hover:text-blue-100 flex items-center justify-center gap-2 group/btn transition-colors duration-150 w-full"
                >
                  <span>View Plans</span>
                  <FaArrowRight className="text-sm group-hover/btn:translate-x-1 transition-transform duration-150" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Card 2: Reset Password - Purple */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.3, delay: 0.5 }}
            whileHover={{ 
              y: -3,
              scale: 1.02,
              transition: { duration: 0.15 }
            }}
            className="relative group overflow-hidden rounded-2xl"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-purple-500 to-purple-600"></div>
            <div className="relative bg-gradient-to-br from-purple-500 to-purple-600 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-150">
              <div className="text-center">
                <motion.div
                  whileHover={{ 
                    scale: 1.05,
                    rotate: -5,
                    transition: { duration: 0.15 }
                  }}
                  className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6 backdrop-blur-sm"
                >
                  <FaKey className="text-white text-2xl" />
                </motion.div>
                <h4 className="text-xl font-bold text-white mb-3">Forgot Password?</h4>
                <p className="text-purple-100 text-sm mb-6 leading-relaxed">
                  Click here to securely reset your password and regain access to your account.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="text-white font-semibold hover:text-purple-100 flex items-center justify-center text-center gap-2 group/btn transition-colors duration-150 w-full"
                >
                  <span>Reset Password</span>
                  <FaArrowRight className="text-sm group-hover/btn:translate-x-1 transition-transform duration-150" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Card 3: Contact Support - Green */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.3, delay: 0.6 }}
            whileHover={{ 
              y: -3,
              scale: 1.02,
              transition: { duration: 0.15 }
            }}
            className="relative group overflow-hidden rounded-2xl"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-green-500 via-green-500 to-green-600"></div>
            <div className="relative bg-gradient-to-br from-green-500 to-green-600 p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-150">
              <div className="text-center">
                <motion.div
                  whileHover={{ 
                    scale: 1.05,
                    rotate: 5,
                    transition: { duration: 0.15 }
                  }}
                  className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6 backdrop-blur-sm"
                >
                  <FaPhoneAlt className="text-white text-2xl" />
                </motion.div>
                <h4 className="text-xl font-bold text-white mb-3">Having Login Issues?</h4>
                <p className="text-green-100 text-sm mb-6 leading-relaxed">
                  Contact our support team for immediate assistance with any login problems.
                </p>
                <a
                  href="tel:9319745997"
                  className="text-white font-semibold hover:text-green-100 flex items-center justify-center gap-2 group/btn transition-colors duration-150"
                >
                  <span>Call: 9319745997</span>
                  <FaArrowRight className="text-sm group-hover/btn:translate-x-1 transition-transform duration-150" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Security & Stats Section */}
        <div className="mx-auto">
          {/* Security Card - Orange */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.3, delay: 0.7 }}
            whileHover={{ 
              scale: 1.01,
              transition: { duration: 0.15 }
            }}
            className="relative group overflow-hidden rounded-2xl mb-8"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-500 to-orange-600"></div>
            <div className="relative bg-gradient-to-br from-orange-300 to-orange-400 p-8 rounded-2xl shadow-xl">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12 justify-center">
                <div className="flex items-center gap-4">
                  <motion.div
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    className="text-3xl"
                  >
                    🔒
                  </motion.div>
                  <div>
                    <h4 className="text-xl font-bold ">Secure Login</h4>
                    <p className="text-sm">
                      OTP authentication & 256-bit SSL encryption
                    </p>
                  </div>
                </div>
                
                <div className="hidden md:block h-10 w-px bg-white/30"></div>
                
                <div className="flex items-center gap-4">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <FaShieldAlt className="text-3xl" />
                  </motion.div>
                  <div>
                    <h4 className="text-xl font-bold">Data Privacy</h4>
                    <p className="text-sm">
                      Your data security is our top priority
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: "24/7", label: "Access", color: "text-white", bg: "bg-gradient-to-br from-blue-500 via-blue-500 to-blue-600", icon: "🌐" },
              { value: "OTP", label: "Secured", color: "text-white", bg: "bg-purple-50 bg-gradient-to-br from-purple-500 via-purple-500 to-purple-600", icon: "✅" },
              { value: "SSL", label: "Encryption", color: "text-white", bg: "bg-green-50 bg-gradient-to-br from-green-500 via-green-500 to-green-600", icon: "🔐" },
              { value: "SEBI", label: "Registered", color: "text-white", bg: "bg-orange-50 bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600", icon: "📋" }
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.2, delay: 0.8 + idx * 0.1 }}
                whileHover={{ 
                  y: -2,
                  scale: 1.03,
                  transition: { duration: 0.15 }
                }}
                className={`${stat.bg} p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-150 text-center`}
              >
                <div className="text-2xl mb-2">{stat.icon}</div>
                <div className={`text-2xl font-bold ${stat.color} mb-1`}>{stat.value}</div>
                <div className="text-white text-sm font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>

        
      </div>
    </section>
  );
};

export default MemberLoginCTA;